<template>
  <BaseModal :state="state" :open="open" :close="onClose">
    <template #content>
      <BaseLoader v-if="loading.planoAcao" />

      <template v-else>
        <q-form
          ref="formRef"
          no-error-focus
          class="flex-1 pb-80 flex flex-col overflow-visible md:overflow-hidden"
        >
          <div
            class="w-full grid grid-cols-2 gap-16 gap-y-24 p-24 overflow-y-auto min-w-0"
          >
            <OInput
              v-model="model.responsavel"
              size="md"
              type="text"
              :label="t('personResponsible')"
              :readonly="isDisabledFields"
              :class="{ readonly: isDisabledFields }"
              :rules="[(value) => value?.length > 0]"
              class="label-transparent"
            />

            <OSelect
              :model-value="model.prioridade"
              :label="t('priority')"
              :options="optionsPrioridade"
              size="md"
              emit-value
              map-options
              option-label="titulo"
              option-value="value"
              class="w-full"
              :readonly="isDisabledFields"
              :class="{ readonly: isDisabledFields }"
              :rules="[(value) => value]"
              @update:model-value="
                (value) => (model.prioridade = value)
              "
            />

            <OInputDateTime
              :data="model.data_inicio"
              :label="t('initialDate')"
              size="md"
              class="text-paragraph-3 text-neutral-70"
              :readonly="isDisabledFields"
              :class="{ readonly: isDisabledFields }"
              :rules="[(value) => value?.length > 0]"
              @update:date="(v) => (model.data_inicio = v)"
            />

            <OInputDateTime
              :data="model.data_entrega"
              :label="t('deadline')"
              size="md"
              class="text-paragraph-3 text-neutral-70"
              :readonly="isDisabledFields"
              :class="{ readonly: isDisabledFields }"
              :rules="[(value) => value?.length > 0]"
              @update:date="(v) => (model.data_entrega = v)"
            />

            <OSelect
              :model-value="model.nivel_alvo"
              :label="t('targetedLevel')"
              size="md"
              :options="alternativas"
              emit-value
              map-options
              option-label="titulo"
              option-value="titulo"
              class="w-full col-span-2 min-w-0"
              :readonly="isDisabledFields"
              :class="{ readonly: isDisabledFields }"
              :rules="[(value) => value?.length > 0]"
              @update:model-value="
                (value) => (model.nivel_alvo = value)
              "
            />

            <AccordionViewInfos :description />

            <OInput
              v-model="model.comentario"
              size="lg"
              type="textarea"
              stack-label
              :label="t('comment')"
              :placeholder="t('writeHere')"
              :readonly="isDisabledFields"
              :class="{ readonly: isDisabledFields }"
              :rules="[(value) => value?.length > 0]"
              class="col-span-2 label-transparent showPlaceholderOnStack"
            />
          </div>

          <q-separator />

          <template v-if="!isDisabledFields">
            <div
              class="w-full h-80 absolute bottom-0 left-0 z-10 bg-card-100 border-t border-neutral-100/10 p-16"
            >
              <div class="flex justify-end gap-8">
                <OButton
                  primary
                  size="md"
                  :label="t('saveAction')"
                  class="text-headline-3 !min-w-96"
                  @click="onSubmit"
                />
              </div>
            </div>
          </template>
        </q-form>
      </template>

      <BaseInnerLoader v-if="loading.enviandoPlanoAcao" />
    </template>
  </BaseModal>
</template>

<script setup>
import { computed, inject, onMounted, ref } from 'vue'
import { NotifyError } from 'js/vue/boot/Notify'
import { useI18n } from 'vue-i18n'
import AccordionViewInfos from 'components/Accordion/AccordionViewInfos.vue'
import BaseInnerLoader from 'components/Loader/BaseInnerLoader.vue'
import BaseLoader from 'components/Loader/BaseLoader.vue'
import BaseModal from 'components/Modal/BaseModal.vue'
import OButton from 'components/Button/OButton.vue'
import OInput from 'components/Input/OInput.vue'
import OInputDateTime from 'components/Input/OInputDateTime.vue'
import OSelect from 'components/Select/OSelect.vue'
import useModal from 'composables/useModal'
import usePerguntas from 'composables/usePerguntas'

const { t } = useI18n()

const modalPlanoAcao = useModal()

const { state, open, close, setState } = modalPlanoAcao

const {
  initialRequestsPlanoAcao,
  planoAcao,
  loading,
  isTabPrevious,
  isCorporateGroup,
} = usePerguntas()

const props = defineProps({
  pergunta: String,
  alternativas: Array,
})

const formRef = ref(null)
const context = inject('context')

const model = ref({
  responsavel: '',
  prioridade: null,
  data_inicio: null,
  data_entrega: null,
  comentario: '',
  nivel_alvo: props.alternativas?.at()?.titulo,
})

const optionsPrioridade = ref([
  { value: 5, titulo: `5 - ${t('highStatus')}` },
  { value: 4, titulo: '4' },
  { value: 3, titulo: '3' },
  { value: 2, titulo: '2' },
  { value: 1, titulo: `1 - ${t('lowStatus')}` },
])

async function onSubmit() {
  const valid = await formRef.value.validate()
  if (!valid) {
    NotifyError(t('fillAllFields'))
    return
  }
  const payload = {
    ...model.value,
    pergunta: +props.pergunta,
    fabricante: +context.user.fabricante_id,
  }
  if (planoAcao.value) {
    emit('update', planoAcao.value.id, payload)
  } else {
    emit('submit', payload)
  }
}

const description = computed(() => {
  const alternativa = props.alternativas?.find(
    (alt) => alt.titulo === model.value.nivel_alvo
  )
  return alternativa ? alternativa.descricao : ''
})

const isDisabledFields = computed(() => {
  return isCorporateGroup.value || isTabPrevious.value
})

onMounted(async () => {
  await initialRequestsPlanoAcao(
    props.pergunta,
    context.user.fabricante_id
  )
  if (planoAcao.value) {
    model.value = {
      ...planoAcao.value,
      prioridade: +planoAcao.value.prioridade,
    }
  }
})

const onClose = () => {
  close()
}

setState({
  header: { title: t('actionPlan') },
  style: { width: '50rem', minHeight: '80vh' },
})

const emit = defineEmits(['submit', 'update'])

defineExpose({
  state,
  open,
  close,
  setState,
})
</script>

<style lang="scss" scoped></style>
